import { http } from '@/lib/http'
import type { TenantLookup } from '@/types/models'

export const tenantApi = {
  /** GET /tenant?mobile="<cc> <number>" → the existing user, or null on 404. */
  lookupByMobile: (mobile: string): Promise<TenantLookup | null> =>
    http.get<TenantLookup>('/tenant', { params: { mobile } }).catch((e: unknown) => {
      if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 404) return null
      throw e
    }),
  /** GET /tenant?tenantId= → the profile plus `kycVerified` (a flag, never the KYC itself). */
  get: (tenantId: string) => http.get<TenantProfile>('/tenant', { params: { tenantId } }),
}

export interface TenantProfile extends TenantLookup {
  kycVerified?: boolean
}

/** One row of GET /tenants. `paymentPending` is computed by the backend but dropped by its projection. */
export interface TenantRow extends TenantLookup {
  leaseIds?: string[]
  property?: { id: string; title: string }
  /** Earliest unpaid rent date across the tenant's leases; absent when nothing is unpaid. */
  nextDueDate?: string
  due?: number
  /** ISO 4217 of `due`. Rows are grouped by tenant and currency, so unscoped a two-country tenant is two rows. */
  currency?: string
  paymentPending?: boolean
}

type TenantListRaw = TenantRow[] | { items?: TenantRow[]; tenants?: TenantRow[]; total?: number } | null

const unwrap = (d: TenantListRaw): TenantRow[] => (Array.isArray(d) ? d : d?.items ?? d?.tenants ?? [])

/** The mobile app asks for one page of 250; the backend ignores sortBy and sorts newest first. */
export const TENANTS_PAGE_SIZE = 250

export const tenantListApi = {
  /** Bare array on the wire. */
  // `country` (LP-491): only that country's leases and rents count; absent = every country.
  list: (searchTerm = '', country?: string) =>
    http.get<TenantListRaw>('/tenants', { params: { page: 1, size: TENANTS_PAGE_SIZE, ...(searchTerm ? { searchTerm } : {}), ...(country ? { country } : {}) } }).then(unwrap),
  /** Owing the most, highest first (server sort). */
  topDue: (size = 6, country?: string) => http.get<TenantListRaw>('/tenants', { params: { page: 1, size, sortBy: 'due', sortOrder: -1, ...(country ? { country } : {}) } }).then(unwrap),
  recent: (size = 10) => http.get<TenantListRaw>('/tenants', { params: { page: 1, size, sortBy: 'firstName', sortOrder: 1 } }).then(unwrap),
}
